// hooks/useSchoolParams.js - VERSION COMPLÈTE AVEC TEMPS RÉEL
import { useState, useEffect } from 'react';

// Fonction utilitaire pour déclencher un événement de mise à jour
const triggerSchoolParamsUpdate = () => {
  window.dispatchEvent(new CustomEvent('schoolParamsUpdated'));
};

const useSchoolParams = () => {
 const [schoolInfo, setSchoolInfo] = useState({
  name: '',
  sous_titre: 'Établissement Scolaire',
  slogan: '',
  address: '',
  city: '',
  postalCode: '',
  country: '',
  phone: '',
  fax: '',
  email: '',
  website: '',
  logo_initiales: 'EC',
  couleur_logo: '#246990',
  logo_url: null,
  titre_accueil: '',
  description_accueil: '',
  plafond_reduction_pourcentage: 100,
  famille_nombreuse_min_enfants: 2,
  famille_nombreuse_delai_regularisation_jours: 7,
  // ✅ AJOUT INFOS TENANT
  tenant_subdomain: 'unknown',
  tenant_display_name: null,
  tenant_school_name: null,
  tenant_id: null
});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


const loadSchoolParams = async () => {
  try {
    setLoading(true);
    
    // ✅ ESSAYER D'ABORD L'ENDPOINT PROTÉGÉ
    let response = await fetch('/api/parametres/ecole', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      credentials: 'include'
    });

    // ✅ SI 403 (pas de permission), ESSAYER L'ENDPOINT PUBLIC
    if (response.status === 403) {
      console.log('⚠️ [useSchoolParams] Accès refusé, tentative endpoint public...');
      
      response = await fetch('/api/parametres-ecole/public', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
    }

    if (!response.ok) {
      throw new Error(`Erreur ${response.status}`);
    }

    const data = await response.json();
    const p = data.data || {};
    
    setSchoolInfo({
      name: p.nom_ecole || '',
      sous_titre: p.sous_titre || 'Établissement Scolaire',
      slogan: p.slogan || '',
      address: p.adresse || '',
      city: p.ville || '',
      postalCode: p.code_postal || '',
      country: p.pays || '',
      phone: p.telephone || '',
      fax: p.fax || '',
      email: p.email || '',
      website: p.site_web || '',
      logo_initiales: p.logo_initiales || 'EC',
      couleur_logo: p.couleur_logo || '#246990',
      logo_url: p.logo_url || null,
      titre_accueil: p.titre_accueil || '',
      description_accueil: p.description_accueil || '',
      plafond_reduction_pourcentage: p.plafond_reduction_pourcentage ?? 100,
      famille_nombreuse_min_enfants: p.famille_nombreuse_min_enfants ?? 2,
      famille_nombreuse_delai_regularisation_jours: p.famille_nombreuse_delai_regularisation_jours ?? 7,
      tenant_subdomain: p.tenant_subdomain || 'unknown',
      tenant_display_name: p.tenant_display_name || null,
      tenant_school_name: p.tenant_school_name || null,
      tenant_id: p.tenant_id || null
    });

  } catch (error) {
    console.error('❌ [useSchoolParams] Erreur:', error);
    setError(error.message);
  } finally {
    setLoading(false);
  }
};


  const updateSchoolParams = async (schoolData) => {
    try {
      const response = await fetch('/api/parametres/ecole', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
        body: JSON.stringify({
          nom_ecole: schoolData.name,
          sous_titre: schoolData.sous_titre,
          slogan: schoolData.slogan,
          adresse: schoolData.address,
          ville: schoolData.city,
          code_postal: schoolData.postalCode,
          pays: schoolData.country,
          telephone: schoolData.phone,
          fax: schoolData.fax,
          email: schoolData.email,
          site_web: schoolData.website,
          logo_initiales: schoolData.logo_initiales,
          couleur_logo: schoolData.couleur_logo,
          logo_url: schoolData.logo_url,
          titre_accueil: schoolData.titre_accueil,
          description_accueil: schoolData.description_accueil,
          plafond_reduction_pourcentage: schoolData.plafond_reduction_pourcentage ?? 100,
          famille_nombreuse_min_enfants: schoolData.famille_nombreuse_min_enfants ?? 2,
          famille_nombreuse_delai_regularisation_jours: schoolData.famille_nombreuse_delai_regularisation_jours ?? 7
        })
      });

      if (!response.ok) {
        // Le serveur explique pourquoi il refuse — « L'annee scolaire 2025-2026
        // est cloturee », « Permission insuffisante ». Ne garder que le code
        // transformait un refus motive en panne sans cause : c'est ce qui a
        // fait chercher un bug la ou il n'y en avait pas.
        let message = `Erreur ${response.status}`;
        try {
          const corps = await response.json();
          if (corps?.message) message = corps.message;
          else if (corps?.error) message = corps.error;
        } catch {
          // Reponse sans corps JSON : le code reste la seule information.
        }
        throw new Error(message);
      }

      await loadSchoolParams(); // Reload
      triggerSchoolParamsUpdate(); // ✅ Déclencher l'événement temps réel
      return { success: true };
      
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const uploadLogo = async (file) => {
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await fetch('/api/parametres/ecole/upload-logo', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
        body: formData
      });

      const result = await response.json();
      
      if (result.success) {
        await loadSchoolParams(); // Reload après upload
        triggerSchoolParamsUpdate(); // ✅ Déclencher l'événement temps réel
        return { success: true, data: result.data };
      } else {
        return { success: false, error: result.message };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteLogo = async () => {
    try {
      const response = await fetch('/api/parametres/ecole/logo', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include'
      });

      const result = await response.json();
      
      if (result.success) {
        await loadSchoolParams(); // Reload après suppression
        triggerSchoolParamsUpdate(); // ✅ Déclencher l'événement temps réel
        return { success: true };
      } else {
        return { success: false, error: result.message };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const handleInputChange = (field, value) => {
    setSchoolInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ✅ Écouter les événements de mise à jour + chargement initial
  useEffect(() => {
    loadSchoolParams();
    
    // Écouter les mises à jour depuis d'autres composants
    const handleUpdate = () => {
      console.log('🔄 [useSchoolParams] Mise à jour temps réel détectée');
      loadSchoolParams();
    };
    
    window.addEventListener('schoolParamsUpdated', handleUpdate);
    
    return () => {
      window.removeEventListener('schoolParamsUpdated', handleUpdate);
    };
  }, []);

  return {
    schoolInfo,
    handleInputChange,
    updateSchoolParams,
    uploadLogo,
    deleteLogo,
    loadSchoolParams,
    loading,
    error
  };
};

export default useSchoolParams;
